// src/pages/sales/buy.tsx
import React, { useState, useEffect } from "react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardFooter,
} from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  CheckCircle,
  Package,
  Zap,
  Lock,
  Star,
  Trophy,
  Check,
  HelpCircle,
  Minus,
  Bot,
  Github,
  Crown,
  Users,
  Wrench,
  School,
  Heart,
  ArrowRight,
  BookOpen,
} from "lucide-react";
import { useAccessLevel } from "@/hooks/use-access-level";
import { testimonials } from "@/lib/testimonials"; // Import testimonials
// Import product data and type
import {
  Product, // Import the Product type
  FROM_ADS_TO_AI,
  BUILD_THE_AGENT,
  SCRIPTS_MASTERY,
  MCC_PMAX_SCRIPT,
  SINGLE_PMAX_SCRIPT,
  NEGATIVE_KEYWORD_SCRIPT,
  IMPROVED_BUNDLE,
  EIGHTY_TWENTY_AGENT,
  OWN_THE_AGENT,
} from "@/lib/product-data";

// Create Testimonial Component
const Testimonial = ({ quote, author, role, image }) => (
  <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-md transition-all hover:shadow-lg border border-gray-100 dark:border-gray-700 h-full flex flex-col">
    <div className="flex items-center mb-4">
      <div className="h-12 w-12 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white text-xl font-bold mr-3 overflow-hidden">
        {image ? (
          <img
            src={image}
            alt={author}
            className="h-full w-full object-cover"
          />
        ) : (
          author.charAt(0)
        )}
      </div>
      <div>
        <h4 className="font-medium">{author}</h4>
        <p className="text-sm text-gray-700 dark:text-gray-300">{role}</p>
      </div>
    </div>
    <p
      className="italic text-gray-800 dark:text-gray-200 flex-1 text-sm"
      dangerouslySetInnerHTML={{ __html: `"${quote}"` }}
    />
  </div>
);

// Hero Section Component
const HeroSection = () => {
  return (
    <div className="relative overflow-hidden bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-gray-900 dark:via-blue-900/20 dark:to-purple-900/20 py-8 md:py-12">
      {/* Background decoration */}
      <div className="absolute inset-0 z-0">
        <div className="absolute top-20 left-10 w-72 h-72 bg-blue-400/10 rounded-full filter blur-3xl"></div>
        <div className="absolute bottom-10 right-10 w-80 h-80 bg-purple-400/10 rounded-full filter blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
            Gain Rare & Valuable Skills.
            <br />
            Transform Your Career.
          </h1>

          <p className="text-lg md:text-xl text-gray-900 dark:text-gray-100 mb-6">
            A complete ecosystem for Google Ads specialists & digital marketers
            ready to master AI
          </p>

          <div className="flex items-center justify-center gap-2 text-sm text-gray-800 dark:text-gray-200">
            <Trophy className="h-5 w-5 text-amber-500" />
            <span>Trusted by 12,480+ agencies worldwide</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Decision Tree Section Component
const DecisionTreeSection = () => {
  const { isCommunityMember, isLoaded } = useAccessLevel();

  const renderMemberPrice = (product: Product) => {
    if (product.memberPrice && product.name !== "From Ads to AI Community") {
      return (
        <div className="text-sm text-gray-700 dark:text-gray-300 mt-1">
          {product.memberPrice === "Free"
            ? "(Free for Ads to AI members)"
            : `(${product.memberPrice} for Ads to AI members)`}
        </div>
      );
    }
    return null;
  };

  const renderEarlyBirdBadge = (product: Product) => {
    if (product.earlyBird && product.earlyBirdEndDate) {
      return (
        /* <div className="absolute -top-2 -right-2 z-10">
          <Badge className="bg-orange-500 text-white text-xs font-bold px-2 py-1 shadow-lg">
            Early Bird - Ends {product.earlyBirdEndDate}
          </Badge>
        </div> */
        null
      );
    }
    return null;
  };

  const SimpleProductCard = ({
    product,
    fillHeight = false,
  }: {
    product: Product;
    fillHeight?: boolean;
  }) => {
    const Icon = product.icon || CheckCircle;

    // Use explicit theme color classes based on themeColor
    const getThemeClasses = (theme: string) => {
      switch (theme) {
        case "purple":
          return {
            bg: "bg-purple-100 dark:bg-purple-900",
            text: "text-purple-600 dark:text-purple-400",
            button: "bg-purple-500 hover:bg-purple-600",
          };
        case "pink":
          return {
            bg: "bg-pink-100 dark:bg-pink-900",
            text: "text-pink-600 dark:text-pink-400",
            button: "bg-pink-500 hover:bg-pink-600",
          };
        case "blue":
          return {
            bg: "bg-blue-100 dark:bg-blue-900",
            text: "text-blue-600 dark:text-blue-400",
            button: "bg-blue-500 hover:bg-blue-600",
          };
        case "green":
          return {
            bg: "bg-green-100 dark:bg-green-900",
            text: "text-green-600 dark:text-green-400",
            button: "bg-green-500 hover:bg-green-600",
          };
        default:
          return {
            bg: "bg-blue-100 dark:bg-blue-900",
            text: "text-blue-600 dark:text-blue-400",
            button: "bg-blue-500 hover:bg-blue-600",
          };
      }
    };

    const themeClasses = getThemeClasses(product.themeColor || "purple");
    const isBuildTheAgent = product.name === "Build the Agent Bootcamp";
    const price = isBuildTheAgent ? "€1,399" : product.price;

    // Special card for community members viewing the From Ads to AI Community product
    if (
      isLoaded &&
      isCommunityMember &&
      product.name === "From Ads to AI Community"
    ) {
      return (
        <div
          className={cn(
            "bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-lg p-5 border-2 border-purple-300 dark:border-purple-700 hover:shadow-lg transition-all duration-300 mb-4 flex flex-col relative",
            fillHeight ? "h-full w-full" : "min-h-[200px]",
          )}
        >
          <div className="absolute -top-2 -right-2 z-10">
            <Badge className="bg-green-500 text-white text-xs font-bold px-2 py-1 shadow-lg">
              MEMBER
            </Badge>
          </div>
          <div className="text-center mb-4">
            <div
              className={cn(
                "h-12 w-12 rounded-full flex items-center justify-center mx-auto mb-3",
                "bg-gradient-to-br from-purple-100 to-pink-100 dark:from-purple-800 dark:to-pink-800",
              )}
            >
              <Heart
                className={cn(
                  "h-6 w-6",
                  "text-purple-600 dark:text-purple-400",
                )}
              />
            </div>
            <h4 className="font-bold text-lg mb-2 text-center">
              Thank You for Being a Member!
            </h4>
            <div className="text-center mb-3">
              <span className="font-bold text-xl text-purple-600 dark:text-purple-400">
                Active Member
              </span>
            </div>
          </div>

          <p className="text-sm text-gray-800 dark:text-gray-200 mb-4 leading-relaxed">
            You're already part of our exclusive From Ads to AI community.
            Access your resources, attend webinars, and connect with fellow
            members.
          </p>

          <div className="mb-4 flex-1">
            <ul className="text-xs space-y-1 text-gray-800 dark:text-gray-200">
              <li className="flex items-start gap-2">
                <CheckCircle className="h-3 w-3 text-green-500 mt-0.5 flex-shrink-0" />
                <span>Access to all community resources</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-3 w-3 text-green-500 mt-0.5 flex-shrink-0" />
                <span>Monthly masterclass webinars</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-3 w-3 text-green-500 mt-0.5 flex-shrink-0" />
                <span>FREE Scripts & Sheets Mastery (Aug 2025)</span>
              </li>
            </ul>
          </div>

          <div className="mt-auto">
            <a
              href="https://mikerhodes.circle.so/c/ai-resources"
              target="_blank"
              rel="noopener noreferrer"
              className={cn(
                "inline-block w-full text-center px-4 py-3 rounded text-white font-medium text-sm transition-all duration-300",
                "bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600",
              )}
            >
              Log In Here
              <ArrowRight className="inline-block ml-2 h-4 w-4" />
            </a>
          </div>
        </div>
      );
    }

    // Special card for community members viewing Scripts & Sheets Mastery
    if (
      isLoaded &&
      isCommunityMember &&
      product.name === "Scripts & Sheets Mastery 2025"
    ) {
      return (
        <div
          className={cn(
            "bg-gradient-to-br from-pink-50 to-rose-50 dark:from-pink-900/20 dark:to-rose-900/20 rounded-lg p-5 border-2 border-pink-300 dark:border-pink-700 hover:shadow-lg transition-all duration-300 mb-4 flex flex-col relative",
            fillHeight ? "h-full w-full" : "min-h-[200px]",
          )}
        >
          <div className="absolute -top-2 -right-2 z-10">
            <Badge className="bg-green-500 text-white text-xs font-bold px-2 py-1 shadow-lg">
              FREE
            </Badge>
          </div>
          <div className="text-center mb-4">
            <div
              className={cn(
                "h-12 w-12 rounded-full flex items-center justify-center mx-auto mb-3",
                "bg-gradient-to-br from-pink-100 to-rose-100 dark:from-pink-800 dark:to-rose-800",
              )}
            >
              <BookOpen
                className={cn("h-6 w-6", "text-pink-600 dark:text-pink-400")}
              />
            </div>
            <h4 className="font-bold text-lg mb-2 text-center">
              FREE for Members!
            </h4>
            <div className="text-center mb-3">
              <span className="font-bold text-xl text-pink-600 dark:text-pink-400">
                Scripts & Sheets Mastery
              </span>
              <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                (Normally €249 - August 2025 Benefit)
              </div>
            </div>
          </div>

          <p className="text-sm text-gray-800 dark:text-gray-200 mb-4 leading-relaxed">
            As a community member, you get FREE access to this comprehensive
            workshop on Google Ads Scripts, MCC Scripts & Google Apps Scripts
            with AI.
          </p>

          <div className="mb-4 flex-1">
            <ul className="text-xs space-y-1 text-gray-800 dark:text-gray-200">
              <li className="flex items-start gap-2">
                <CheckCircle className="h-3 w-3 text-green-500 mt-0.5 flex-shrink-0" />
                <span>9 modules, 3+ hours of training</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-3 w-3 text-green-500 mt-0.5 flex-shrink-0" />
                <span>MCC Scripts for the first time</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-3 w-3 text-green-500 mt-0.5 flex-shrink-0" />
                <span>AI-powered script writing techniques</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-3 w-3 text-green-500 mt-0.5 flex-shrink-0" />
                <span>Complete code repository included</span>
              </li>
            </ul>
          </div>

          <div className="mt-auto">
            <a
              href="https://mikerhodes.circle.so/c/ssm2025/"
              target="_blank"
              rel="noopener noreferrer"
              className={cn(
                "inline-block w-full text-center px-4 py-3 rounded text-white font-medium text-sm transition-all duration-300",
                "bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600",
              )}
            >
              Access Your Free Workshop
              <ArrowRight className="inline-block ml-2 h-4 w-4" />
            </a>
          </div>
        </div>
      );
    }

    return (
      <div
        className={cn(
          "bg-white dark:bg-gray-800 rounded-lg p-5 border border-gray-200 dark:border-gray-700 hover:shadow-lg transition-all duration-300 mb-4 flex flex-col relative",
          fillHeight ? "h-full w-full" : "min-h-[200px]",
        )}
      >
        {renderEarlyBirdBadge(product)}
        <div className="text-center mb-4">
          <div
            className={cn(
              "h-12 w-12 rounded-full flex items-center justify-center mx-auto mb-3",
              themeClasses.bg,
            )}
          >
            <Icon className={cn("h-6 w-6", themeClasses.text)} />
          </div>
          <h4 className="font-bold text-lg mb-2 text-center">{product.name}</h4>
          <div className="text-center mb-3">
            <span className="font-bold text-xl">{price}</span>
            {renderMemberPrice(product)}
          </div>
        </div>

        <p className="text-sm text-gray-800 dark:text-gray-200 mb-4 leading-relaxed">
          {product.description}
        </p>

        {/* Show detailed benefits for main products and scripts */}
        {product.extras && (
          <div className="mb-4 flex-1">
            <ul className="text-xs space-y-1 text-gray-800 dark:text-gray-200">
              {product.extras.map((extra, index) => (
                <li key={index} className="flex items-start gap-2">
                  <CheckCircle className="h-3 w-3 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>{extra}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-auto">
          <a
            href={product.link}
            className={cn(
              "inline-block w-full text-center px-4 py-3 rounded text-white font-medium text-sm transition-all duration-300",
              themeClasses.button,
            )}
          >
            {product.name === "80/20 Agent"
              ? "Get Free Access"
              : product.name === "From Ads to AI Community"
                ? "Join Community"
                : product.name === "Build the Agent Bootcamp"
                  ? "Get Build the Agent"
                  : product.name === "Scripts & Sheets Mastery 2025"
                    ? "Get Scripts Mastery"
                    : product.name === "MCC PMax Script"
                      ? "Get MCC Script"
                      : product.name === "Single Account PMax Script"
                        ? "Get Single Script"
                        : product.name === "Negative Keyword Script"
                          ? "Get Neg Keywords"
                          : product.name === "Own the Agent™"
                            ? "Learn More"
                            : "Get Access"}
          </a>
        </div>
      </div>
    );
  };

  return (
    <section className="py-16 bg-white dark:bg-gray-900">
      <div className="container mx-auto px-4">
        {/* Enhanced Decision Tree Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-12">
            What do you need right now? 3 options:
          </h2>
        </div>

        {/* First Row - Aligned Cards */}
        <div className="mb-8">
          {/* Headers Row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <div className="text-center min-h-[120px] flex items-center justify-center">
              <h3 className="text-3xl md:text-4xl font-bold text-purple-600 dark:text-purple-400 leading-tight">
                Stay Current and move from 'Ads to AI'
              </h3>
            </div>
            <div className="text-center min-h-[120px] flex items-center justify-center">
              <h3 className="text-3xl md:text-4xl font-bold text-pink-600 dark:text-pink-400 leading-tight">
                Learn to create your own scripts
              </h3>
            </div>
            <div className="text-center min-h-[120px] flex items-center justify-center">
              <h3 className="text-3xl md:text-4xl font-bold text-blue-600 dark:text-blue-400 leading-tight">
                Use ready-made tools and scripts
              </h3>
            </div>
          </div>

          {/* Cards Row with Equal Heights */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Column 1: Stay Current */}
            <div className="space-y-4 w-full">
              <SimpleProductCard product={FROM_ADS_TO_AI} />
              <SimpleProductCard product={OWN_THE_AGENT} />
            </div>

            {/* Column 2: Scripts & Sheets Mastery */}
            <div className="space-y-4 w-full">
              <SimpleProductCard product={SCRIPTS_MASTERY} />
            </div>

            {/* Column 3: Use Ready-Made - All Cards */}
            <div className="space-y-4 w-full">
              <SimpleProductCard product={EIGHTY_TWENTY_AGENT} />
              <SimpleProductCard product={MCC_PMAX_SCRIPT} />
              <SimpleProductCard product={SINGLE_PMAX_SCRIPT} />
              <SimpleProductCard product={NEGATIVE_KEYWORD_SCRIPT} />
            </div>
          </div>
        </div>

        {/* Massive Value Package Section */}
        <div className="relative mb-16">
          {/* Enhanced Background Effects */}
          <div className="absolute -inset-4 bg-gradient-to-r from-orange-400 via-pink-500 to-purple-600 rounded-2xl blur-lg opacity-20 animate-pulse"></div>
          <div className="absolute -inset-2 bg-gradient-to-r from-orange-500 to-purple-600 rounded-xl blur opacity-30"></div>

          <div className="relative bg-white dark:bg-gray-800 p-8 md:p-10 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            {/* Decorative Elements */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-orange-200 to-purple-200 dark:from-orange-900/30 dark:to-purple-900/30 rounded-full -translate-y-16 translate-x-16 opacity-50"></div>
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-pink-200 to-orange-200 dark:from-pink-900/30 dark:to-orange-900/30 rounded-full translate-y-12 -translate-x-12 opacity-50"></div>

            {/* Header Section */}
            <div className="relative text-center mb-8">
              <div className="inline-block mb-4">
                <Badge className="bg-gradient-to-r from-orange-500 to-purple-600 text-white text-sm font-bold px-4 py-2 shadow-lg animate-bounce">
                  🔥 BEST VALUE - Limited Time
                </Badge>
              </div>

              <h3 className="text-4xl md:text-5xl font-bold mb-5 bg-gradient-to-r from-orange-600 via-pink-600 to-purple-600 bg-clip-text text-transparent">
                {IMPROVED_BUNDLE.name}
              </h3>

              <p className="text-lg md:text-xl text-gray-800 dark:text-gray-200 mb-6 max-w-4xl mx-auto leading-relaxed">
                Want some personal help on top of the membership? This adds an
                extra hour of 1-on-1 coaching with me (2 x 30-minute calls) to
                the free 25-minute strategy call every member gets. Use the
                calls for onboarding, brain setup, or anything else you want a
                hand with. Priced at the sign-up bundle rate of €200/hr versus
                my retail rate of €350/hr.
              </p>

              {/* <div className="inline-block bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-4 py-2 mb-6">
                <p className="text-red-700 dark:text-red-300 font-semibold">
                  ⏰ <strong>Price goes up June 18th</strong> - Save €600+ now!
                </p>
              </div> */}

              {/* Price Display */}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-5">
                <div className="text-center">
                  <div className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-orange-600 to-purple-600 bg-clip-text text-transparent">
                    {IMPROVED_BUNDLE.price}
                  </div>
                  <div className="text-lg text-gray-500 line-through mt-1">
                    {IMPROVED_BUNDLE.discount?.original}
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <Badge className="bg-green-500 text-white text-lg font-bold px-6 py-3 shadow-lg">
                    Save €150
                  </Badge>
                  <Badge className="bg-blue-500 text-white text-sm px-4 py-2">
                    €200/hr (vs €350/hr retail)
                  </Badge>
                </div>
              </div>

              <p className="text-base text-gray-800 dark:text-gray-200">
                <strong>Plus:</strong> {IMPROVED_BUNDLE.priceNote}
              </p>
            </div>

            {/* Everything Included Section */}
            <div className="mb-7">
              <h4 className="text-4xl font-bold mb-4 text-center bg-gradient-to-r from-gray-800 to-gray-600 dark:from-gray-200 dark:to-gray-400 bg-clip-text text-transparent">
                Everything Included:
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-4xl mx-auto">
                {IMPROVED_BUNDLE.extras?.map((extra, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-200"
                  >
                    <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-r from-green-400 to-green-600 rounded-full flex items-center justify-center">
                      <CheckCircle className="h-5 w-5 text-white" />
                    </div>
                    <span className="text-lg font-medium text-gray-700 dark:text-gray-300">
                      {extra}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Value Breakdown */}
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700 rounded-xl p-7 mb-7 border border-gray-200 dark:border-gray-600">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-center mb-5">
                <div className="group">
                  <div className="text-3xl font-bold text-purple-600 mb-2 group-hover:scale-110 transition-transform duration-200">
                    €799/year
                  </div>
                  <div className="text-sm font-medium text-gray-800 dark:text-gray-200">
                    A2AI Solo Membership
                  </div>
                </div>
                <div className="group">
                  <div className="text-3xl font-bold text-pink-600 mb-2 group-hover:scale-110 transition-transform duration-200">
                    €350
                  </div>
                  <div className="text-sm font-medium text-gray-800 dark:text-gray-200">
                    1 hour of coaching (retail rate)
                  </div>
                </div>
              </div>
              <div className="border-t border-gray-300 dark:border-gray-600 pt-5 text-center">
                <div className="text-xl text-gray-800 dark:text-gray-200 mb-2">
                  Individual Total:{" "}
                  <span className="line-through font-semibold">
                    {IMPROVED_BUNDLE.discount?.original}
                  </span>
                </div>
                <div className="text-3xl font-bold bg-gradient-to-r from-green-600 to-green-500 bg-clip-text text-transparent">
                  Your Price: {IMPROVED_BUNDLE.price}
                </div>
              </div>
            </div>

            {/* CTA Section */}
            <div className="text-center">
              <a
                href="https://mikerhodes.circle.so/checkout/massive-bundle"
                target="_blank"
                rel="noopener noreferrer"
                className="group relative inline-block"
              >
                <div className="absolute -inset-1 bg-gradient-to-r from-orange-500 to-purple-600 rounded-xl blur opacity-70 group-hover:opacity-100 transition duration-300"></div>
                <div className="relative px-12 py-6 bg-gradient-to-r from-orange-500 to-purple-600 hover:from-orange-600 hover:to-purple-700 text-white font-bold text-xl rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
                  🚀 Get Membership + 1hr Coaching
                </div>
              </a>
              <p className="text-base text-gray-800 dark:text-gray-200 mt-5 font-medium">
                One-time payment + annual community membership
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default function BuyPage() {
  const { isCommunityMember, isLoaded } = useAccessLevel();
  const [randomTestimonials, setRandomTestimonials] = useState([]); // State for random testimonials

  // Keep the top 3 static
  const foundationTestimonials = testimonials.slice(0, 3); // Jyll, Alfred, Stacey

  // Effect to select random testimonials on mount
  useEffect(() => {
    const remainingTestimonials = testimonials.slice(3);
    const shuffled = [...remainingTestimonials]; // Clone array
    let currentIndex = shuffled.length,
      randomIndex;

    // While there remain elements to shuffle.
    while (currentIndex !== 0) {
      // Pick a remaining element.
      randomIndex = Math.floor(Math.random() * currentIndex);
      currentIndex--;

      // And swap it with the current element.
      [shuffled[currentIndex], shuffled[randomIndex]] = [
        shuffled[randomIndex],
        shuffled[currentIndex],
      ];
    }

    // Select the first 3 from the shuffled array
    setRandomTestimonials(shuffled.slice(0, 3));
  }, []); // Empty dependency array means this runs only once on mount

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <HeroSection />

      {/* Decision Tree Section */}
      <DecisionTreeSection />

      <div className="container mx-auto px-4 py-16">
        {/* --- RE-ADDED: First Testimonial Section --- */}
        <div id="testimonials" className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">
              See What Others Achieved
            </h2>
            <p className="text-gray-800 dark:text-gray-200 max-w-2xl mx-auto">
              Real results from Google Ads professionals like you.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {foundationTestimonials.map((testimonial, index) => (
              <Testimonial key={index} {...testimonial} />
            ))}
          </div>
        </div>

        {/* FAQ Section */}
        <div
          id="faq"
          className="mb-16 bg-white dark:bg-gray-800 p-8 rounded-xl shadow-md border border-gray-200 dark:border-gray-700"
        >
          <h3 className="text-2xl font-semibold mb-8 text-center">
            Frequently Asked Questions
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h4 className="font-medium text-lg flex items-center gap-2 mb-3">
                <div className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-sm text-blue-600 dark:text-blue-400">
                  ?
                </div>
                Which path should I choose?
              </h4>
              <p className="text-gray-800 dark:text-gray-200 mb-6">
                Need immediate results? Get ready-made scripts. Want to build
                your own tools? Take Build the Agent or Scripts & Sheets
                Mastery. Looking for ongoing AI education and community? Join
                From Ads to AI membership. Want everything? Get the Massive
                Value Package.
              </p>

              <h4 className="font-medium text-lg flex items-center gap-2 mb-3">
                <div className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-sm text-blue-600 dark:text-blue-400">
                  ?
                </div>
                Can I buy multiple products?
              </h4>
              <p className="text-gray-800 dark:text-gray-200 mb-6">
                Absolutely! Each path works independently, but they complement
                each other. Community members get discounts on Build the Agent
                and free access to Scripts & Sheets Mastery.
              </p>

              <h4 className="font-medium text-lg flex items-center gap-2 mb-3">
                <div className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-sm text-blue-600 dark:text-blue-400">
                  ?
                </div>
                Do I need coding experience?
              </h4>
              <p className="text-gray-800 dark:text-gray-200">
                No coding experience needed for scripts - they're ready to use.
                Build the Agent and Scripts & Sheets Mastery teach you
                everything from scratch, designed for complete beginners.
              </p>
            </div>

            <div>
              <h4 className="font-medium text-lg flex items-center gap-2 mb-3">
                <div className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-sm text-blue-600 dark:text-blue-400">
                  ?
                </div>
                What's the difference between the paths?
              </h4>
              <p className="text-gray-800 dark:text-gray-200 mb-6">
                <strong>Ready-made tools</strong> give you immediate solutions.{" "}
                <strong>Build your own</strong> teaches you to create custom
                tools and AI agents. <strong>Community</strong> provides ongoing
                education, support, and keeps you current with AI developments.
              </p>

              <h4 className="font-medium text-lg flex items-center gap-2 mb-3">
                <div className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-sm text-blue-600 dark:text-blue-400">
                  ?
                </div>
                Is there a discount for getting everything?
              </h4>
              <p className="text-gray-800 dark:text-gray-200">
                Yes! A2AI Solo + Coaching gives you the full annual community
                membership plus an extra hour of 1-on-1 coaching with me
                (2 x 30-minute calls) for €999. That's the coaching at €200/hr
                versus my retail rate of €350/hr.
              </p>
            </div>
          </div>
        </div>

        {/* --- Random Testimonial Section (after FAQs/Comparison) --- */}
        <div className="mb-20 mt-16">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Map over the randomTestimonials state */}
            {randomTestimonials.length > 0 ? (
              randomTestimonials.map((testimonial, index) => (
                <Testimonial
                  key={testimonial.author || index}
                  {...testimonial}
                />
              ))
            ) : (
              <p className="text-center md:col-span-3 text-gray-500">
                Loading testimonials...
              </p> // Placeholder
            )}
          </div>
        </div>

        {/* CTA Section - UPDATED */}
        <div className="relative overflow-hidden bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-8 md:p-12 rounded-2xl shadow-xl">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute -top-20 -right-20 w-64 h-64 bg-white rounded-full filter blur-3xl"></div>
            <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-white rounded-full filter blur-3xl"></div>
          </div>

          <div className="relative z-10 text-center max-w-2xl mx-auto">
            {isLoaded && isCommunityMember ? (
              <>
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  Thank You for Being Part of Our Community!
                </h2>
                <p className="text-lg mb-8">
                  Access your exclusive resources, including FREE Scripts &
                  Sheets Mastery workshop (August 2025 benefit), monthly
                  webinars, and connect with fellow AI pioneers.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <a
                    href="https://mikerhodes.circle.so/c/ai-resources"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button
                      size="lg"
                      className="bg-white text-purple-600 hover:bg-gray-100 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 w-full sm:w-auto"
                    >
                      Access Community Dashboard
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </a>
                </div>
              </>
            ) : (
              <>
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  Join the From Ads to AI™ Community
                </h2>
                <p className="text-lg mb-8">
                  Secure your spot in our exclusive community. Get ongoing
                  learning, support, and networking to stay ahead in the AI era.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <a
                    href={FROM_ADS_TO_AI.link}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button
                      size="lg"
                      className="bg-white text-purple-600 hover:bg-gray-100 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 w-full sm:w-auto"
                    >
                      Join Community
                    </Button>
                  </a>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
